import classNames from 'classnames';
import type { KeyboardEvent, ReactElement, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { uuid4 } from 'sumi-hash';
import type { UUID } from 'sumi-hash';
import whatInput from 'what-input';
import styles from './accordion/Accordion.module.scss';

type Heading = (param: { isOpen: boolean }) => ReactElement | ReactElement[];

export type Props = {
  heading: Heading;
  body: ReactNode;
  openByDefault?: boolean;
  onToggle?: (isVisible: boolean) => void;
};

export function Accordion(props: Props): ReactNode {
  const { heading, body, openByDefault, onToggle } = props;
  const [isOpen, setIsOpen] = useState(!!openByDefault);
  const [accordionHeaderElementId, setAccordionHeaderElementId] = useState<
    UUID | undefined
  >(undefined);
  const [accordionBodyElementId, setAccordionBodyElementId] = useState<
    UUID | undefined
  >(undefined);

  useEffect(() => {
    // We want server-side and client-side markup to match before mount.
    // The alternative would be to pass a pregenerated UUID from the parent.
    setAccordionHeaderElementId(`accordion-header-${uuid4()}`);
    setAccordionBodyElementId(`accordion-segment-${uuid4()}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want to run once
  }, []);

  function toggleBodyElementVisibility() {
    const nextIsOpen = !isOpen;
    setIsOpen(nextIsOpen);
    onToggle?.(nextIsOpen);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const keys = whatInput.keys();
    if (keys.indexOf('space') > -1 || keys.indexOf('enter') > -1) {
      toggleBodyElementVisibility();
      event.preventDefault();
    }
  }

  return (
    <div>
      <div
        id={accordionHeaderElementId}
        onClick={toggleBodyElementVisibility}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="tab"
        aria-expanded={isOpen}
        aria-controls={accordionBodyElementId}
        className={styles.heading}
      >
        {heading({ isOpen })}
      </div>
      <section
        className={classNames({
          [styles.body]: true,
          [styles.closed]: !isOpen
        })}
        hidden={!isOpen}
        id={accordionBodyElementId}
        aria-labelledby={accordionHeaderElementId}
      >
        {isOpen && body}
      </section>
    </div>
  );
}
